import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';

import { Platform } from '../platform/_types';
import { User } from '../user/_types';

import { PlatformOnboarding } from './_types';

export const fetchOnboardingKey = (platformId: Platform['id']) => [
  'platform-onboarding',
  platformId,
];

export function denormalizePlatformOnboarding(
  data: Partial<PlatformOnboarding>
): any {
  const decamelized = decamelizeKeys(data, { deep: true }) as any;
  if (decamelized.twilio10_dlc_data) {
    decamelized.twilio_10dlc_data = decamelized.twilio10_dlc_data;
    delete decamelized.twilio10_dlc_data;
  }
  if (decamelized.color_theme) {
    removeReadOnlyFields(decamelized.color_theme, ['app_primary_color']);
  }
  return decamelized;
}

export function normalizePlatformOnboarding(data: any): PlatformOnboarding {
  const camelized = camelcaseKeys(data, { deep: true }) as any;
  if (camelized.twilio10dlcData) {
    camelized.twilio10DlcData = camelized.twilio10dlcData;
    delete camelized.twilio10dlcData;
  }
  return camelized;
}

export async function fetchOnboarding(
  platformId: Platform['id']
): Promise<PlatformOnboarding> {
  const response = await api.get(`/api/v4/platform_onboardings/${platformId}`);
  return normalizePlatformOnboarding(response.data);
}

export async function updateOnboarding(
  id: Platform['id'],
  data: Partial<PlatformOnboarding>
): Promise<PlatformOnboarding> {
  const response = await api.patch(`/api/v4/platform_onboardings/${id}`, {
    platform_onboarding: denormalizePlatformOnboarding(data),
  });
  return normalizePlatformOnboarding(response.data);
}

export const getAppIconsDownloadUrl = (platformId: Platform['id']) =>
  `/api/v4/platform_onboardings/${platformId}/download_app_icons`;

export const getLegacyAppIconsDownloadUrl = (platformId: Platform['id']) =>
  `/api/v4/platform_onboardings/${platformId}/download_legacy_app_icons`;

interface SearchSalesforceAccountRequest {
  id: Platform['id'];
  query: string;
}

export async function searchSalesforceAccounts({
  id,
  query,
}: SearchSalesforceAccountRequest): Promise<any[]> {
  const response = await api.get(
    `/api/v4/platform_onboardings/${id}/search_salesforce_accounts`,
    {
      params: {
        query,
      },
    }
  );

  return camelcaseKeys(response.data.accounts, { deep: true });
}

export async function searchSalesforceUsers(
  id: Platform['id'],
  query: string
): Promise<any[]> {
  const response = await api.get(
    `/api/v4/platform_onboardings/${id}/search_salesforce_users`,
    {
      params: { query },
    }
  );

  return camelcaseKeys(response.data.users, { deep: true });
}

interface LinkSalesforceAccountRequest {
  id: Platform['id'];
  salesforceData: LinkSalesForceData;
}

export interface LinkSalesForceData {
  platformId: Identifier;
  email?: string;
  accountId?: string;
  tsmId?: string;
}

export async function linkSalesforceAccount({
  id,
  salesforceData,
}: LinkSalesforceAccountRequest): Promise<PlatformOnboarding> {
  const response = await api.post(
    `/api/v4/platform_onboardings/${id}/link_salesforce_account`,
    decamelizeKeys(salesforceData, { deep: true })
  );

  return camelcaseKeys(response.data, { deep: true });
}

export interface UpdateServiceAccountKeyRequest {
  serviceAccountKey: string;
}

export interface UpdateAppleApiKeyRequest {
  appleApiKey: string;
  appleIssuerId: string;
  appleKeyId: string;
  applePushKey: string;
  applePushKeyId: string;
}

export async function updateAppleApiKey(
  userId: User['id'],
  data: UpdateAppleApiKeyRequest
): Promise<void> {
  const response = await api.post(
    `/api/v4/users/${userId}/update_apple_api_key`,
    {
      apple_api_key: data.appleApiKey,
      apple_issuer_id: data.appleIssuerId,
      apple_key_id: data.appleKeyId,
      apple_push_key: data.applePushKey,
      apple_push_key_id: data.applePushKeyId,
    }
  );

  return camelcaseKeys(response.data, { deep: true });
}

export async function updateServiceAccountKey(
  userId: User['id'],
  data: UpdateServiceAccountKeyRequest
): Promise<void> {
  await api.post(`/api/v4/users/${userId}/update_service_account_key`, {
    service_account_key: data.serviceAccountKey,
  });
}
export interface TwilioNumber {
  number: string;
  // add other properties if needed
}

interface ProvisionNumberResponse {
  number: string;
  // add other properties if needed
}

export async function fetchAvailableNumbers(): Promise<TwilioNumber[]> {
  const response = await api.get('/api/v4/twilio/available_numbers');
  return camelcaseKeys(response.data, { deep: true });
}

export async function provisionNumber(
  phoneNumber: string,
  platformId: Platform['id']
): Promise<ProvisionNumberResponse> {
  const response = await api.post('/api/v4/twilio/provision_number', {
    phone_number: phoneNumber,
    platform_id: platformId,
  });
  return camelcaseKeys(response.data, { deep: true });
}

export async function createBundleId(
  platformId: Platform['id']
): Promise<void> {
  await api.post(`/api/v4/platform_onboardings/${platformId}/create_bundle_id`);
}

export async function setupFirebase(platformId: Platform['id']): Promise<void> {
  await api.post(`/api/v4/platform_onboardings/${platformId}/setup_firebase`);
}

export interface FirebaseSetupStatus {
  status: string;
  error?: string;
}

export async function updateIosArnCert(
  platformId: Platform['id']
): Promise<void> {
  await api.post(
    `/api/v4/platform_onboardings/${platformId}/update_ios_arn_cert`
  );
}

export async function resetAppStoreSetup(
  platformId: Platform['id']
): Promise<PlatformOnboarding> {
  const response = await api.post(
    `/api/v4/platform_onboardings/${platformId}/reset_app_store_setup`
  );
  return camelcaseKeys(response.data, { deep: true });
}

export interface StepCompletionNotification {
  stepId: string;
  stepName: string;
}

export async function notifyStepCompletion(
  platformId: Platform['id'],
  data: StepCompletionNotification
): Promise<void> {
  return api.post(
    `/api/v4/platform_onboardings/${platformId}/notify_step_completion`,
    decamelizeKeys(data, { deep: true })
  );
}

export async function createManagedAppsSubscription(
  platformId: Platform['id'],
  planType: 'monthly' | 'annual'
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await api.post(
    `/api/v4/platform_onboardings/${platformId}/create_managed_apps_subscription`,
    { plan_type: planType }
  );
  return response.data;
}

// Domain verification methods
export interface DomainVerification {
  domain?: string;
  dns?: {
    verification: boolean;
    domain: boolean;
    acme?: boolean;
  };
  mapping?: boolean;
  token?: {
    token: string;
    verificationMethod: string;
  };
  cloudflare?: {
    customHostnameId?: string;
    status?: string;
    sslStatus?: string;
    createdAt?: string;
  };
  [key: string]: unknown;
}

export interface EmailVerification {
  domain?: string;
  dkim: { valid: boolean };
  dkim2: { valid: boolean };
  dmarc: { valid: boolean };
  validSigning: boolean;
  verifiedAt?: string;
  verifyTxtKey?: string;
  [key: string]: unknown;
}

export async function startDomainVerification(
  trainerId: User['id'],
  domain: string
): Promise<DomainVerification> {
  const response = await api.get(
    `/api/v4/users/${trainerId}/start_domain_verification/?domain=${domain}`
  );
  return camelcaseKeys(response.data, { deep: true }) as DomainVerification;
}

export async function verifyDomain(
  trainerId: User['id'],
  domain: string
): Promise<DomainVerification> {
  const response = await api.get(
    `/api/v4/users/${trainerId}/verify_domain/?domain=${domain}`
  );
  return camelcaseKeys(response.data, { deep: true }) as DomainVerification;
}

export async function cancelDomainVerification(
  trainerId: User['id']
): Promise<{ success: boolean }> {
  const response = await api.get(
    `/api/v4/users/${trainerId}/cancel_domain_verification/`
  );
  return camelcaseKeys(response.data, { deep: true }) as { success: boolean };
}

export async function validateCnames(
  trainerId: User['id']
): Promise<DomainVerification> {
  const response = await api.get(`/api/v4/users/${trainerId}/validate_cnames`);
  return camelcaseKeys(response.data, { deep: true }) as DomainVerification;
}

export async function startSendingEmailVerification(
  trainerId: User['id'],
  domain: string
): Promise<EmailVerification> {
  const response = await api.get(
    `/api/v4/users/${trainerId}/start_sending_email_verification/?domain=${domain}`
  );
  return camelcaseKeys(response.data, { deep: true }) as EmailVerification;
}

export async function updateAndroidBundleId(
  platformId: Platform['id'],
  androidBundleId: string
): Promise<{
  success: boolean;
  android_bundle_id?: string;
  firebase_android_app_id?: string;
  error?: string;
}> {
  const response = await api.post(
    `/api/v4/platform_onboardings/${platformId}/update_android_bundle_id`,
    { android_bundle_id: androidBundleId }
  );
  return camelcaseKeys(response.data, { deep: true });
}

export const androidSigningKeys = [
  'exercise-android',
  'weighttraining-android',
  'upload-android',
];
